import React, { useEffect, useState, useRef, useMemo } from "react";
import { useConsultantVisibleContext } from "../../../features/consultant/screenContextAdapter";
import "./heatmap.css";
import HeatmapChart from "./HeatmapChart";
import HeatmapStatsChart from "./HeatmapStatsChart";
import HeatmapTable from "./HeatmapTable";
import { getHeatmap } from "../../../redux/apiCalls/analysisCall/analysisCall";
import { useDispatch, useSelector } from "react-redux";
import FrequencyComponent from "../../../components/FilterFrequency/FilterFrequency";
import DragDropDevices from "../../../components/Measurement/DragDropDevices";
import GroupSelector from "../../../components/Measurement/GroupSelector";
import DragDropCompanies from "../../../components/Measurement/DragDropCompanies";
import MeasurementContext from "../../../context/MeasurementContext";
import { useMeasurementGroups } from "../../../hooks/useMeasurementGroups";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { scaleByMagnitude } from "../../../helpers/scaleUnits";
import { Check } from "lucide-react";
import { getInitialDatesFromStorageOrToday } from "../../../helpers/measurement-secGroup";
import { useImageExport } from "../../../hooks/useImageExport";
import ChartControls from "../../../components/Measurement/ChartControls";

const HeatmapPage = () => {
  const graphRef = useRef(null);
  const exportNameRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userAccessToken = useSelector(
    (state) => state.user.currentUser?.accessToken
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [frec, setFrec] = useState("30min");
  const [firstSelect, setFirstSelect] = useState([]);
  const [devicesSelected, setDevicesSelected] = useState([]);
  const [meterSelected, setMeterSelected] = useState(null);
  const [openDropdown, setOpenDropdown] = useState({});

  const [isInitialized, setIsInitialized] = useState(false);
  const [appliedState, setAppliedState] = useState(null);
  const [appliedMeter, setAppliedMeter] = useState(null);
  useConsultantVisibleContext({ ...(appliedState || { firstSelect, devicesSelected, startDate, endDate }),
    selected_metric: (appliedMeter || meterSelected)?.label || (appliedMeter || meterSelected)?.tag,
    selected_unit: (appliedMeter || meterSelected)?.unit, selected_chart: "Mapa de calor" });

  const [showReportInput, setShowReportInput] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(true);

  const { captureScreen, exportToPDF, handleImageExport } = useImageExport(
    firstSelect,
    meterSelected,
    startDate,
    endDate
  );

  const { allGroups, primary, setPrimary, second, setSecond } =
    useMeasurementGroups(dispatch, userAccessToken, { firstSelect });

  const contextValue = useMemo(
    () => ({ meterSelected, setMeterSelected }),
    [meterSelected]
  );

  useEffect(() => {
    const { startDate: initialStart, endDate: initialEnd } =
      getInitialDatesFromStorageOrToday();

    setStartDate(initialStart);
    setEndDate(initialEnd);

    try {
      const savedStr = localStorage.getItem("consumption_state");
      if (savedStr) {
        const saved = JSON.parse(savedStr);

        if (saved.frec) setFrec(saved.frec);
        if (Array.isArray(saved.devicesSelected)) {
          setDevicesSelected(saved.devicesSelected);
        }
        if (saved.meterSelected) setMeterSelected(saved.meterSelected);
        if (saved.primary) setPrimary(saved.primary);
        if (saved.secondary) setSecond(saved.secondary);
      }
    } catch (e) {
      console.error("Error restaurando consumption_state", e);
    }

    setIsInitialized(true);
  }, [setPrimary, setSecond]);

  useEffect(() => {
    if (!isInitialized) return;

    if (startDate && endDate && devicesSelected.length && meterSelected?.tag) {
      fetchHeatmap();
    }
  }, [isInitialized]);

  const fetchHeatmap = async () => {
    if (!devicesSelected.length || !startDate || !endDate || !meterSelected?.tag) {
      return;
    }

    try {
      setLoading(true);

      const macs = devicesSelected.map((d) => d.mac).join(",");

      const payload = {
        startDate: startDate ? startDate.toISOString() : null,
        endDate: endDate ? endDate.toISOString() : null,
        frec,
        devicesSelected,
        meterSelected,
        primary,
        secondary: second,
      };

      localStorage.setItem("consumption_state", JSON.stringify(payload));

      setAppliedState({
        startDate,
        endDate,
        frec,
        devicesSelected,
        meterSelected,
        primary,
        secondary: second,
      });

      const response = await getHeatmap(
        dispatch,
        userAccessToken,
        startDate,
        endDate,
        frec,
        macs,
        {},
        meterSelected.tag,
        "TOTAL"
      );

      setData(response);
      setAppliedMeter(meterSelected.tag);
    } catch (err) {
      console.error("Error loading heatmap:", err);
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = useMemo(() => {
    if (!appliedState) return true;

    return (
      appliedState.frec !== frec ||
      appliedState.startDate?.toString() !== startDate?.toString() ||
      appliedState.endDate?.toString() !== endDate?.toString() ||
      JSON.stringify(appliedState.devicesSelected) !==
        JSON.stringify(devicesSelected) ||
      appliedState.meterSelected?.tag !== meterSelected?.tag
    );
  }, [frec, startDate, endDate, devicesSelected, meterSelected, appliedState]);

  const scaleInfo = useMemo(() => {
    if (!data?.stats || !meterSelected?.tag) {
      return { factor: 1, unit: "" };
    }

    const hours = Object.keys(data.stats);

    const max = hours.map((h) => data.stats[h]?.max ?? null);
    const min = hours.map((h) => data.stats[h]?.min ?? null);
    const avg = hours.map((h) => data.stats[h]?.avg ?? null);
    const median = hours.map((h) => data.stats[h]?.median ?? null);

    return scaleByMagnitude([max, min, avg, median], meterSelected.tag);
  }, [data, meterSelected]);

  const handleDownloadImage = async () => {
    try {
      await captureScreen();
    } catch (error) {
      console.error("Error al descargar imagen del heatmap:", error);
    }
  };

  const handleExportPdf = async () => {
    try {
      await exportToPDF(graphRef);
    } catch (error) {
      console.error("Error al exportar PDF del heatmap:", error);
    }
  };

  const handleExportClick = () => {
    setShowReportInput((prev) => !prev);
    setButtonVisible(false);
  };

  const handleSendToReports = async () => {
    try {
      await handleImageExport(graphRef, {});
      setShowReportInput(false);
    } catch (error) {
      console.error("Error al enviar heatmap a reportes:", error);
    }
  };

  return (
    <MeasurementContext.Provider value={contextValue}>
      <div className="container-heatmap">
        <div className="page-content">
          <div className="cont-path">
            <p className="path path-cursor" onClick={() => navigate(`/`)}>
              <FormattedMessage id="Dashboard" defaultMessage="Dashboard" />
              {"> "}
            </p>
            <p className="path">Mapa de calor</p>
          </div>

          <div className="measurement">
            <DragDropCompanies
              setFirstSelect={setFirstSelect}
              firstSelect={firstSelect}
            />

            <GroupSelector
              allGroups={allGroups}
              primary={primary}
              setPrimary={setPrimary}
              second={second}
              setSecond={setSecond}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
              firstSelect={firstSelect}
            />

            <DragDropDevices
              setDevicesSelected={setDevicesSelected}
              firstSelect={firstSelect}
              primary={primary}
              second={second}
              prefillDevices={devicesSelected}
            />

            <div className="heatmap-row-filter-btn">
              <FrequencyComponent
                onValuesChange={(newFrec, newStart, newEnd) => {
                  setFrec(newFrec);
                  setStartDate(newStart);
                  setEndDate(newEnd);
                }}
                startDate={startDate}
                endDate={endDate}
                frec={frec}
                isAnalysis={true}
              />

              {hasChanges && (
                <button className="heatmap-btn" onClick={fetchHeatmap}>
                  <Check size={14} style={{ marginRight: "4px" }} />
                  Aplicar
                </button>
              )}
            </div>

            {loading && <p>Cargando...</p>}

            {data && (
              <>
                <div id="graph" ref={graphRef}>
                  <HeatmapChart
                    data={data}
                    factor={scaleInfo.factor}
                    unit={scaleInfo.unit}
                    param={appliedMeter}
                  />
                </div>

                <ChartControls
                  onCaptureScreen={handleDownloadImage}
                  onExportPDF={handleExportPdf}
                  showExportInput={showReportInput}
                  onExportClick={handleExportClick}
                  onExportSubmit={handleSendToReports}
                  buttonVisible={buttonVisible}
                  nameInputRef={exportNameRef}
                  hideChartTypes={true}
                  hideCustomDashboard={true}
                />

                <div className="heatmap-stats-section">
                  <h2 className="heatmap-section-title">
                    Análisis estadístico por franja horaria
                  </h2>
                  <p className="heatmap-section-subtitle">
                    Máximo, mínimo, promedio, mediana y variabilidad para cada
                    hora del día.
                  </p>

                  <HeatmapStatsChart
                    stats={data.stats}
                    factor={scaleInfo.factor}
                    unit={scaleInfo.unit}
                  />

                  <p className="heatmap-section-subtitle">Resumen</p>
                  <HeatmapTable
                    stats={data.stats}
                    factor={scaleInfo.factor}
                    unit={scaleInfo.unit}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </MeasurementContext.Provider>
  );
};

export default HeatmapPage;
